import { useEffect, useRef, useState } from 'react'
import { flushSync } from 'react-dom'
import { useLocation, useNavigate } from 'react-router-dom'
import { Avatar, Button, cn, CountBadge, IconButton } from '@platform/ui-kit'
import { useAuth } from '@/app/providers/AuthProvider'
import { ICONS } from '@/app/routes/moduleRegistry'
import { currentTheme, subscribeTheme, switchTheme, type Theme } from './theme'

/* ------------------------------------------------------------------
   TopBar — سند مادر بند ۱۱.۱
   جست‌وجو، اعلان‌ها، پروفایل، خروج و تغییر پوسته
   ------------------------------------------------------------------ */

interface TopBarProps {
  onToggleSidebar: () => void
  onOpenSearch: () => void
  notificationCount: number
}

export function TopBar({ onToggleSidebar, onOpenSearch, notificationCount }: TopBarProps) {
  const { user, logout } = useAuth()
  const navigate = useNavigate()
  const { pathname } = useLocation()
  const [menuOpen, setMenuOpen] = useState(false)

  /*
    🔴 در صفحهٔ گفتگو زنگِ اعلان‌های پنل نشان داده نمی‌شود (صاحبِ پروژه،
    ۱۴۰۵/۰۷/۲۹): کاربر آن را با اعلانِ پیام‌های همان گفتگو یکی می‌گرفت.
    اعلان‌ها از منوی کناری و هر صفحهٔ دیگر در دسترس‌اند.
  */
  const onChat = pathname === '/app/chat' || pathname.startsWith('/app/chat/')
  const menuRef = useRef<HTMLDivElement>(null)

  /* بستن منو با کلیک بیرون یا Escape */
  useEffect(() => {
    if (!menuOpen) return

    const onPointerDown = (event: MouseEvent) => {
      if (!menuRef.current?.contains(event.target as Node)) setMenuOpen(false)
    }
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setMenuOpen(false)
    }

    document.addEventListener('mousedown', onPointerDown)
    document.addEventListener('keydown', onKeyDown)
    return () => {
      document.removeEventListener('mousedown', onPointerDown)
      document.removeEventListener('keydown', onKeyDown)
    }
  }, [menuOpen])

  const handleLogout = async () => {
    setMenuOpen(false)
    await logout()
    navigate('/login', { replace: true })
  }

  return (
    <header
      /* هم‌رنگ سایدبار: از توکن‌های --pf-nav-* تغذیه می‌شود نه سطوح
         روشن. چون این نوار در هر دو پوسته تیره می‌ماند، فرزندانش هم
         باید از همان خانواده رنگ بگیرند و نه از --pf-text/--pf-surface
         که در پوسته روشن تقریباً مشکی روی تیره می‌شوند. */
      className={cn(
        'sticky top-0 flex h-[var(--pf-topbar-h)] shrink-0 items-center gap-2 bg-[var(--pf-nav-bg)]/95 px-4 backdrop-blur sm:px-[var(--pf-content-pad)]',
        /* 🔴 در گوشی صفحهٔ گفتگو فقط نوارِ خودش را دارد، مثلِ واتساپ و تلگرام؛
           «≡» ِ همان نوار منوی پنل را باز می‌کند (`features/chat/shellEvents.ts`). */
        onChat && 'max-md:hidden',
      )}
      style={{ zIndex: 'var(--pf-z-topbar)' }}
    >
      <IconButton
        variant="nav"
        size="sm"
        label="باز و بسته کردن منو"
        className="lg:hidden"
        onClick={onToggleSidebar}
        icon={
          /* tabler: menu-2 */
          <svg viewBox="0 0 24 24" fill="none" className="size-5 shrink-0" aria-hidden="true">
            <path d="M4 6l16 0" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" />
            <path d="M4 12l16 0" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" />
            <path d="M4 18l16 0" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" />
          </svg>
        }
      />

      {/* جست‌وجوی یکپارچه — بند ۱۲.۳ */}
      <button
        type="button"
        onClick={onOpenSearch}
        className={cn(
          /* min-w-0 لازم است: بدون آن جعبه جست‌وجو کوچک‌تر از محتوایش
             نمی‌شود و در موبایل فشار روی دکمه‌های آیکونی کنارش می‌افتد. */
          'flex h-9 min-w-0 flex-1 items-center gap-2 rounded-[var(--pf-radius)] border border-[var(--pf-nav-divider)]',
          'bg-[var(--pf-nav-elevated)] px-3 text-[length:var(--pf-text-sm)] text-[var(--pf-nav-label)]',
          'transition-colors duration-[var(--pf-duration-fast)] hover:text-[var(--pf-nav-text)]',
          'max-w-md',
        )}
      >
        {ICONS.search}
        {/* truncate تا در عرض کم، متن به خط دوم نرود و نوار را بشکند */}
        <span className="flex-1 truncate text-start">جست‌وجو در سامانه…</span>
        <kbd className="hidden rounded-[var(--pf-radius-sm)] border border-[var(--pf-nav-divider)] bg-[var(--pf-nav-bg)] px-1.5 py-0.5 text-[length:var(--pf-text-2xs)] text-[var(--pf-nav-label)] sm:inline-block">
          Ctrl K
        </kbd>
      </button>

      <div className="flex-1" />

      <ThemeToggle />

      {!onChat && (
        <div className="relative shrink-0">
          <IconButton
            variant="nav"
            size="sm"
            label="اعلان‌ها"
            onClick={() => navigate('/app/notifications')}
            /* آیکون رجیستری ۱۸px است — اندازه درست برای فهرست فشرده
               سایدبار. اینجا کنار ماه و منو که ۲۰px هستند کوچک می‌افتد،
               پس فقط در همین نوار به ۲۰px بزرگ می‌شود. */
            className="[&_svg]:size-5"
            icon={ICONS.bell}
          />
          {notificationCount > 0 && (
            <CountBadge
              count={notificationCount}
              /* نوار حالا تیره است — قرمز اصلی رویش فقط ۲٫۷۳ کنتراست
                 دارد و نشان گم می‌شود؛ پله روشن‌تر ۳٫۵۹ می‌گیرد. */
              tone="nav-danger"
              className="pointer-events-none absolute -top-0.5 -end-0.5"
            />
          )}
        </div>
      )}

      {/* پروفایل */}
      <div className="relative shrink-0" ref={menuRef}>
        <button
          type="button"
          onClick={() => setMenuOpen((value) => !value)}
          aria-haspopup="menu"
          aria-expanded={menuOpen}
          className="flex shrink-0 items-center gap-2 rounded-[var(--pf-radius)] p-1 transition-colors hover:bg-[var(--pf-nav-elevated)]"
        >
          <Avatar
            name={user?.display_name ?? '—'}
            src={user?.avatar_url}
            fallbackSrc="/icons/3d/avatar-default.png"
            size="sm"
          />
          <span className="hidden min-w-0 text-start sm:block">
            <span className="block max-w-[10rem] truncate text-[length:var(--pf-text-sm)] font-[var(--pf-weight-medium)] text-[var(--pf-nav-text-hover)]">
              {user?.display_name}
            </span>
            <span className="block max-w-[10rem] truncate text-[length:var(--pf-text-2xs)] text-[var(--pf-nav-label)]">
              {user?.employment?.position_title ?? 'بدون سمت'}
            </span>
          </span>
        </button>

        {menuOpen && (
          <div
            role="menu"
            /* ‎.pf-pop-enter‎: از زیرِ دکمه باز می‌شود، نه یک‌باره (tokens.css) */
            className="pf-pop-enter absolute end-0 mt-1.5 w-60 origin-top-left overflow-hidden rounded-[var(--pf-radius-lg)] border border-[var(--pf-border)] bg-[var(--pf-surface)] shadow-[var(--pf-shadow-lg)]"
            style={{ zIndex: 'var(--pf-z-overlay)' }}
          >
            <div className="border-b border-[var(--pf-border)] px-3.5 py-3">
              <p className="truncate text-[length:var(--pf-text-sm)] font-[var(--pf-weight-medium)] text-[var(--pf-text)]">
                {user?.display_name}
              </p>
              <p dir="ltr" className="truncate text-start text-[length:var(--pf-text-xs)] text-[var(--pf-text-muted)]">
                {user?.email ?? user?.mobile}
              </p>
              {user?.employment && (
                <p className="mt-1 truncate text-[length:var(--pf-text-2xs)] text-[var(--pf-text-subtle)]">
                  {user.employment.org_unit_name} · کد {user.employment.employee_no}
                </p>
              )}
            </div>

            {/* هشدار جانشینی — بند ۷: acting_as باید همیشه دیده شود */}
            {user?.acting_as && (
              <div className="border-b border-[var(--pf-border)] bg-[var(--pf-warning-50)] px-3.5 py-2">
                <p className="text-[length:var(--pf-text-xs)] text-[var(--pf-warning-700)]">
                  در حال اقدام به‌جای {user.acting_as.display_name}
                </p>
              </div>
            )}

            <div className="p-1.5">
              {/*
                🔴 ۱۴۰۵/۰۸/۱۲ صاحبِ پروژه: «دکمهٔ پروفایل … وقتی کلیک می‌شه بفرسته کاربر به صفحهٔ کارکنان و بره رو
                خودش». پیش‌تر به ‎/app/profile‎ می‌رفت که چنین صفحه‌ای نیست. حسابِ بی پرونده (مثلاً مدیرِ سیستمِ
                جدا از کارکنان) این ردیف را نمی‌بیند.
              */}
              {user?.employment?.employee_id && (
                <MenuItem
                  label="پروفایل من"
                  onClick={() => {
                    setMenuOpen(false)
                    navigate(`/app/people/employees/${user.employment?.employee_id}`)
                  }}
                />
              )}
              <MenuItem
                label="تنظیمات"
                onClick={() => {
                  setMenuOpen(false)
                  navigate('/app/settings')
                }}
              />
              <MenuItem
                label="تغییر رمز عبور"
                onClick={() => {
                  setMenuOpen(false)
                  navigate('/app/profile/password')
                }}
              />
            </div>

            <div className="border-t border-[var(--pf-border)] p-1.5">
              <Button variant="ghost" size="sm" fullWidth className="justify-start text-[var(--pf-danger-text)]" onClick={handleLogout}>
                خروج از حساب
              </Button>
            </div>
          </div>
        )}
      </div>
    </header>
  )
}

function MenuItem({ label, onClick }: { label: string; onClick: () => void }) {
  return (
    <button
      type="button"
      role="menuitem"
      onClick={onClick}
      className="flex w-full items-center rounded-[var(--pf-radius)] px-2.5 py-1.5 text-start text-[length:var(--pf-text-sm)] text-[var(--pf-text)] transition-colors hover:bg-[var(--pf-surface-hover)]"
    >
      {label}
    </button>
  )
}

/* ------------------------------------------------------------------
   تغییر پوسته روشن/تاریک — ترجیح در localStorage نگه داشته می‌شود.
   این داده حساس نیست و ذخیره آن مشکلی ندارد؛ ممنوعیت بند ۶.۱
   فقط درباره Token است.
   ------------------------------------------------------------------ */

function ThemeToggle() {
  const [theme, setTheme] = useState<Theme>(currentTheme)

  /* تعویض و view transition در `theme.ts` است؛ این دکمه فقط آیکونش را همراه می‌کند — از هر جا که عوض شود
     (همین دکمه یا دکمهٔ کنارِ «+» در گفتگوی گوشی). ⚠️ flushSync: درونِ view transition صدا زده می‌شود و
     آیکون باید پیش از گرفتنِ تصویرِ «بعد» عوض شده باشد. */
  useEffect(() => subscribeTheme((next) => flushSync(() => setTheme(next))), [])

  const toggle = () => switchTheme(theme === 'light' ? 'dark' : 'light')

  return (
    <IconButton
      variant="nav"
      size="sm"
      label={theme === 'light' ? 'پوسته تاریک' : 'پوسته روشن'}
      onClick={toggle}
      icon={
        theme === 'light' ? (
          /* tabler: moon */
          <svg viewBox="0 0 24 24" fill="none" className="size-5 shrink-0" aria-hidden="true">
            <path
              d="M12 3c.132 0 .263 0 .393 0a7.5 7.5 0 0 0 7.92 12.446a9 9 0 1 1 -8.313 -12.454l0 .008"
              stroke="currentColor"
              strokeWidth="1.75"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        ) : (
          /* tabler: sun */
          <svg viewBox="0 0 24 24" fill="none" className="size-5 shrink-0" aria-hidden="true">
            <path
              d="M8 12a4 4 0 1 0 8 0a4 4 0 1 0 -8 0"
              stroke="currentColor"
              strokeWidth="1.75"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
            <path
              d="M3 12h1m8 -9v1m8 8h1m-9 8v1m-6.4 -15.4l.7 .7m12.1 -.7l-.7 .7m0 11.4l.7 .7m-12.1 -.7l-.7 .7"
              stroke="currentColor"
              strokeWidth="1.75"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        )
      }
    />
  )
}
